import { Euler } from 'three';

export const VIOLATION_ICON_COLOR = '#d8584f';

export const FONT_URL_REGULAR = `${import.meta.env.BASE_URL}asset/font/NotoSansKR/NotoSansKR-Regular.ttf`;
export const FONT_URL_BOLD = `${import.meta.env.BASE_URL}asset/font/NotoSansKR/NotoSansKR-Bold.ttf`;
export const PCD_MAX_LIDARS = 128; // PCD 셰이더 uniform 배열 라이다 캡. 128 초과 시 DataTexture 방식이 필요합니다.
export const POINT_ROTATION = new Euler(Math.PI / 2, 0, 0);

export const PLAY_SPEED_LIST = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 4];

export type GridInterval = {
  distance: number; // 거리
  distanceSquared: number; // 거리의 제곱
  gap: number; // 그리드 간격
  fontSize?: number; // fontSize=1, 폰트 크기 배율
};
export const GRID_INTERVALS: GridInterval[] = [
  {
    distance: 300,
    distanceSquared: 300 * 300,
    gap: 50,
    fontSize: 6,
  },
  {
    distance: 140,
    distanceSquared: 140 * 140,
    gap: 20,
    fontSize: 4,
  },
  {
    distance: 80,
    distanceSquared: 80 * 80,
    gap: 10,
    fontSize: 2,
  },
  {
    distance: 20,
    distanceSquared: 20 * 20,
    gap: 5,
    fontSize: 1,
  },
  {
    distance: 10,
    distanceSquared: 10 * 10,
    gap: 1,
    fontSize: 0.25,
  },
] as const;

export const STEP_SIZE_MOVE_ROTATION = [
  // 0.01, 0.05, 0.1, 0.5, 1
  {
    value: 0.01,
    label: '0.01',
    // label: '0.01 m (°)',
  },
  {
    value: 0.05,
    label: '0.05',
  },
  {
    value: 0.1,
    label: '0.1',
  },
  {
    value: 0.5,
    label: '0.5',
  },
  {
    value: 1,
    label: '1',
  },
];

export const STEP_SIZE_MOVE = [
  // 0.01, 0.05, 0.1, 0.5, 1
  {
    value: 0.01,
    label: '0.01m',
  },
  {
    value: 0.05,
    label: '0.05m',
  },
  {
    value: 0.1,
    label: '0.1m',
  },
  {
    value: 0.5,
    label: '0.5m',
  },
  {
    value: 1,
    label: '1m',
  },
];

export const STEP_SIZE_ROTATION = [
  // 0.1, 0.5, 1, 2, 5 degree
  {
    value: 0.1,
    label: '0.1°',
  },
  {
    value: 0.5,
    label: '0.5°',
  },
  {
    value: 1,
    label: '1°',
  },
  {
    value: 2,
    label: '2°',
  },
  {
    value: 5,
    label: '5°',
  },
];

export const RECORD_CHUNK_DEFAULT_SIZE = 5; // 5gb
export const RECORD_CHUNK_MAX_SIZE = 30; // 30gb
export const RECORD_AUTHOR_MAX_LENGTH = 100; // 100 characters
export const RECORD_DURATION_MAX_HOUR = 24; // 24 hours
export const RECORD_SCHEDULE_MAX_HOUR = 24; // 예약 녹화 시작 시각은 24:00까지만 허용

export const RECORD_SCENE_LIST = [
  // None, Alley, Highway, Indoor, Intersection, Office, Plaza, Simulation, Square, Urban, Parking lot, Construction, Proving ground
  { value: 'None', label: 'None' },
  { value: 'Alley', label: 'Alley' },
  { value: 'Highway', label: 'Highway' },
  { value: 'Indoor', label: 'Indoor' },
  { value: 'Intersection', label: 'Intersection' },
  { value: 'Office', label: 'Office' },
  { value: 'Plaza', label: 'Plaza' },
  { value: 'Simulation', label: 'Simulation' },
  { value: 'Square', label: 'Square' },
  { value: 'Urban', label: 'Urban' },
  { value: 'ParkingLot', label: 'Parking lot' },
  { value: 'Construction', label: 'Construction' },
  { value: 'ProvingGround', label: 'Proving ground' },
];

export const RECORD_WEATHER_LIST = [
  { value: 'None', label: 'None' },
  { value: 'Sunny', label: 'Sunny' },
  { value: 'Rain', label: 'Rain' },
  { value: 'Snow', label: 'Snow' },
  { value: 'Fog', label: 'Fog' },
  { value: 'HardRain', label: 'Hard rain' },
  { value: 'HardSnow', label: 'Hard snow' },
];

export const DEFAULT_FLAG_PROPS = {
  COLOR: '#00C679',
  SIZE: 10,
  TITLE: 'Flag',
  VISIBLE: true,
} as const;

/**
 * 캔버스에서 객체아이디 축약해서 볼 때 길이
 */
export const SHORTEN_ID_LENGTH = 4;

export const USER_ROLE_LIST = [
  { value: 'None', label: 'None', i18nKey: 'member.role.neopaleozoic' },
  { value: 'Admin', label: 'Admin', i18nKey: 'member.role.neoparaffin' },
  { value: 'SiteAdmin', label: 'SiteAdmin', i18nKey: 'member.role.neophilism' },
  { value: 'Normal', label: 'Normal', i18nKey: 'member.role.neophilologist' },
] as const;

export const USER_ROLES = USER_ROLE_LIST.map(item => item.value);
export type UserRole = (typeof USER_ROLES)[number];

export const REMOTE_SUPPORT_DURATION_LIST = [
  '15m',
  '30m',
  '1h',
  '2h',
  '3h',
] as const;

/** Drive 모드 기본 화면입니다. */
export const DRIVE_PATH = '/drive';

/** 핸들 버튼으로 들어가는 Drive 화면입니다. 키오스크 시작 URL 로도 씁니다 (VPF-5299). */
export const DRIVE_PROSPECTOR_PATH = '/drive/prospector';
